import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, FileText, Search } from "lucide-react";
import { downloadPdf } from "@/lib/downloadPdf";
import { toast } from "sonner";

interface Simulacao {
  id: string;
  created_at: string;
  user_id: string | null;
  marca: string;
  modelo: string;
  ano: number;
  km: number;
  leiloeiro: string;
  score_condicao: number | null;
  nivel_dano: string | null;
  valor_fipe: number;
  valor_estimado: number;
  modo: string;
}

function nivelBadgeVariant(nivel: string | null) {
  if (!nivel) return "secondary" as const;
  const l = nivel.toLowerCase();
  if (l.includes("leve") || l.includes("baixo")) return "default" as const;
  if (l.includes("moderado") || l.includes("médio") || l.includes("medio")) return "secondary" as const;
  return "destructive" as const;
}

const formatCurrency = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
  });

export default function AdminSimulacoes() {
  const [simulacoes, setSimulacoes] = useState<Simulacao[]>([]);
  const [profiles, setProfiles] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      const [simsRes, profsRes] = await Promise.all([
        supabase
          .from("simulacoes")
          .select("id, created_at, user_id, marca, modelo, ano, km, leiloeiro, score_condicao, nivel_dano, valor_fipe, valor_estimado, modo")
          .order("created_at", { ascending: false }),
        supabase.from("profile").select("user_id, full_name, email"),
      ]);

      if (simsRes.error) {
        toast.error("Erro ao carregar simulações");
        console.error(simsRes.error);
      }

      setSimulacoes(simsRes.data || []);

      const map: Record<string, string> = {};
      profsRes.data?.forEach((p) => {
        map[p.user_id] = p.full_name !== "—" ? p.full_name : (p.email || p.user_id);
      });
      setProfiles(map);
      setLoading(false);
    }
    load();
  }, []);

  const filtered = simulacoes.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.marca.toLowerCase().includes(q) ||
      s.modelo.toLowerCase().includes(q) ||
      s.leiloeiro.toLowerCase().includes(q) ||
      (profiles[s.user_id || ""] || "").toLowerCase().includes(q) ||
      s.ano.toString().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold">Simulações</h1>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por marca, modelo, leiloeiro, usuário..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <p className="text-muted-foreground text-center py-12">Carregando...</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Veículo</TableHead>
                    <TableHead>Leiloeiro</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead className="text-center">Dano</TableHead>
                    <TableHead className="text-right">FIPE</TableHead>
                    <TableHead className="text-right">Estimado</TableHead>
                    <TableHead>Modo</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                        Nenhuma simulação encontrada.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((sim) => (
                      <TableRow key={sim.id}>
                        <TableCell className="whitespace-nowrap text-xs">{formatDate(sim.created_at)}</TableCell>
                        <TableCell className="max-w-[140px] truncate text-xs">
                          {profiles[sim.user_id || ""] || sim.user_id?.slice(0, 8) || "Anônimo"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm font-medium">
                          {sim.marca} {sim.modelo} {sim.ano}
                        </TableCell>
                        <TableCell className="text-xs">{sim.leiloeiro}</TableCell>
                        <TableCell className="text-center">
                          {sim.score_condicao != null ? (
                            <span className="font-semibold">{sim.score_condicao}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={nivelBadgeVariant(sim.nivel_dano)}>
                            {sim.nivel_dano || "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-xs whitespace-nowrap">
                          {formatCurrency(sim.valor_fipe)}
                        </TableCell>
                        <TableCell className="text-right text-xs whitespace-nowrap font-medium">
                          {formatCurrency(sim.valor_estimado)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={sim.modo === "pro" ? "default" : "secondary"}>
                            {sim.modo}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 justify-center">
                            <Button variant="ghost" size="sm" title="Ver detalhes" onClick={() => toast.info("Detalhes em breve")}>
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Baixar PDF"
                              disabled={downloadingId === sim.id}
                              onClick={async () => {
                                setDownloadingId(sim.id);
                                await downloadPdf(sim.id);
                                setDownloadingId(null);
                              }}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
